import React, { useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ExternalPlatformModal from "../../components/ExternalPlatformModal";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexDirection: "column",
		alignItems: "stretch",
		padding: theme.spacing(2),
		height: "100%",
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
	},
	sectionPaper: {
		padding: theme.spacing(2),
		marginBottom: 12,
	},
	sectionHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(2),
	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},

}));

const Settings = () => {
	const classes = useStyles();

	const [settings, setSettings] = useState([]);
	const [externalPlatforms, setExternalPlatforms] = useState([]);
	const [platformModalOpen, setPlatformModalOpen] = useState(false);
	const [selectedPlatform, setSelectedPlatform] = useState(null);
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	const fetchExternalPlatforms = async () => {
		try {
			const { data } = await api.get("/external-platforms");
			setExternalPlatforms(data || []);
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		fetchExternalPlatforms();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					aux[settingIndex].value = data.setting.value;
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const { value } = settings.find(s => s.key === key);
		return value;
	};

	const handleOpenPlatformModal = platform => {
		setSelectedPlatform(platform || null);
		setPlatformModalOpen(true);
	};

	const handleClosePlatformModal = () => {
		setSelectedPlatform(null);
		setPlatformModalOpen(false);
	};

	const handleOpenDeleteConfirmation = platform => {
		setSelectedPlatform(platform);
		setDeleteConfirmationOpen(true);
	};

	const handleDeletePlatform = async () => {
		if (!selectedPlatform) return;

		try {
			await api.delete(`/external-platforms/${selectedPlatform.id}`);
			toast.success("Plataforma externa removida.");
			setSelectedPlatform(null);
			fetchExternalPlatforms();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<MainHeader>
				<Title description="Ajuste preferências gerais e parâmetros da plataforma.">
					{i18n.t("settings.title")}
				</Title>
			</MainHeader>
			<Container className={classes.container} maxWidth="sm">
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Paper className={classes.sectionPaper}>
					<div className={classes.sectionHeader}>
						<div>
							<Typography variant="h6">Plataformas externas</Typography>
							<Typography variant="body2" color="textSecondary">
								Cadastre APIs externas para transferir conversas entre plataformas.
							</Typography>
						</div>
						<Button
							color="primary"
							variant="contained"
							startIcon={<AddIcon />}
							onClick={() => handleOpenPlatformModal(null)}
						>
							Nova
						</Button>
					</div>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Nome</TableCell>
								<TableCell>URL da API</TableCell>
								<TableCell>Token</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Acoes</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{externalPlatforms.map(platform => (
								<TableRow key={platform.id}>
									<TableCell>{platform.name}</TableCell>
									<TableCell>{platform.baseUrl}</TableCell>
									<TableCell>{platform.token}</TableCell>
									<TableCell>
										<Chip
											size="small"
											color={platform.active ? "primary" : "default"}
											label={platform.active ? "Ativa" : "Inativa"}
										/>
									</TableCell>
									<TableCell align="right">
										<Tooltip title="Editar">
											<IconButton
												size="small"
												onClick={() => handleOpenPlatformModal(platform)}
											>
												<EditIcon fontSize="small" />
											</IconButton>
										</Tooltip>
										<Tooltip title="Excluir">
											<IconButton
												size="small"
												onClick={() => handleOpenDeleteConfirmation(platform)}
											>
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
							{externalPlatforms.length === 0 && (
								<TableRow>
									<TableCell colSpan={5}>
										Nenhuma plataforma externa cadastrada.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</Paper>

			</Container>
			<ExternalPlatformModal
				open={platformModalOpen}
				onClose={handleClosePlatformModal}
				platform={selectedPlatform}
				onSaved={fetchExternalPlatforms}
			/>
			<ConfirmationModal
				title="Excluir plataforma externa"
				open={deleteConfirmationOpen}
				onClose={setDeleteConfirmationOpen}
				onConfirm={handleDeletePlatform}
			>
				Tem certeza que deseja excluir esta plataforma externa?
			</ConfirmationModal>
		</div>
	);
};

export default Settings;
